/**
 * Flight Search Component
 * Handles flight search form functionality and validation
 */

import { FlightSearchData } from '../types/data-models';
import { validateRequired, validateDate, validateFutureDate } from '../utils/simple-validation';

export class FlightSearchComponent {
  private form: HTMLFormElement;
  private originInput!: HTMLInputElement;
  private destinationInput!: HTMLInputElement;
  private departureDateInput!: HTMLInputElement;
  private returnDateInput!: HTMLInputElement;
  private passengersSelect!: HTMLSelectElement;
  private submitButton!: HTMLButtonElement;
  private generalErrorDiv!: HTMLElement;

  public onSearch: ((data: FlightSearchData) => void) | null = null;

  constructor(form: HTMLFormElement) {
    this.form = form;
    this.initializeElements();
    this.bindEvents();
    this.setMinDates();
  }

  private initializeElements(): void {
    this.originInput = this.form.querySelector('#origin') as HTMLInputElement;
    this.destinationInput = this.form.querySelector('#destination') as HTMLInputElement;
    this.departureDateInput = this.form.querySelector('#departure-date') as HTMLInputElement;
    this.returnDateInput = this.form.querySelector('#return-date') as HTMLInputElement;
    this.passengersSelect = this.form.querySelector('#passengers') as HTMLSelectElement;
    this.submitButton = this.form.querySelector('.flight-search-form__submit') as HTMLButtonElement;
    this.generalErrorDiv = this.form.querySelector('.flight-search-form__general-error') as HTMLElement;
  }

  private bindEvents(): void {
    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Real-time validation
    this.originInput.addEventListener('blur', () => this.validateField('origin'));
    this.destinationInput.addEventListener('blur', () => this.validateField('destination'));
    this.departureDateInput.addEventListener('change', () => {
      this.validateField('departureDate');
      this.updateReturnDateMin();
    });
    this.returnDateInput.addEventListener('change', () => this.validateField('returnDate'));
    this.passengersSelect.addEventListener('change', () => this.validateField('passengers'));

    // Clear errors on input
    [this.originInput, this.destinationInput, this.departureDateInput, this.returnDateInput, this.passengersSelect]
      .forEach(input => {
        input.addEventListener('input', () => this.clearFieldError(input.name));
      });
  }

  private setMinDates(): void {
    const todayParts = new Date().toISOString().split('T');
    const today = todayParts[0] || '';
    if (this.departureDateInput) {
      this.departureDateInput.min = today;
    }
    if (this.returnDateInput) {
      this.returnDateInput.min = today;
    }
  }

  private updateReturnDateMin(): void {
    if (this.departureDateInput.value) {
      this.returnDateInput.min = this.departureDateInput.value;
      
      // Clear return date if it's before departure date
      if (this.returnDateInput.value && this.returnDateInput.value < this.departureDateInput.value) {
        this.returnDateInput.value = '';
      }
    }
  }

  private validateField(fieldName: string): boolean {
    let isValid = true;
    let errorMessage = '';

    switch (fieldName) {
      case 'origin':
        if (!validateRequired(this.originInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez saisir une ville ou un aéroport de départ';
        } else if (this.originInput.value.length < 2) {
          isValid = false;
          errorMessage = 'Le lieu de départ doit contenir au moins 2 caractères';
        }
        break;

      case 'destination':
        if (!validateRequired(this.destinationInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez saisir une ville ou un aéroport de destination';
        } else if (this.destinationInput.value.length < 2) {
          isValid = false;
          errorMessage = 'La destination doit contenir au moins 2 caractères';
        } else if (this.destinationInput.value.toLowerCase() === this.originInput.value.toLowerCase()) {
          isValid = false;
          errorMessage = 'La destination doit être différente du lieu de départ';
        }
        break;

      case 'departureDate':
        if (!validateRequired(this.departureDateInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez sélectionner une date de départ';
        } else if (!validateDate(this.departureDateInput.value)) {
          isValid = false;
          errorMessage = 'Veuillez saisir une date valide';
        } else if (!validateFutureDate(this.departureDateInput.value)) {
          isValid = false;
          errorMessage = 'La date de départ doit être dans le futur';
        }
        break;

      case 'returnDate':
        if (this.returnDateInput.value) {
          if (!validateDate(this.returnDateInput.value)) {
            isValid = false;
            errorMessage = 'Veuillez saisir une date de retour valide';
          } else if (this.departureDateInput.value && this.returnDateInput.value < this.departureDateInput.value) {
            isValid = false;
            errorMessage = 'La date de retour doit être après la date de départ';
          }
        }
        break;

      case 'passengers':
        if (!validateRequired(this.passengersSelect.value)) {
          isValid = false;
          errorMessage = 'Veuillez sélectionner le nombre de voyageurs';
        } else {
          const passengers = parseInt(this.passengersSelect.value);
          if (passengers < 1 || passengers > 8) {
            isValid = false;
            errorMessage = 'Le nombre de voyageurs doit être entre 1 et 8';
          }
        }
        break;
    }

    this.displayFieldError(fieldName, isValid ? '' : errorMessage);
    return isValid;
  }

  private displayFieldError(fieldName: string, message: string): void {
    const errorElementId = `${fieldName.replace(/([A-Z])/g, '-$1').toLowerCase()}-error`;
    const errorElement = document.getElementById(errorElementId);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = message ? 'block' : 'none';
    }

    // Update field styling
    const field = this.form.querySelector(`[name="${fieldName}"]`) as HTMLElement;
    if (field) {
      if (message) {
        field.classList.add('form-input--error');
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.classList.remove('form-input--error');
        field.setAttribute('aria-invalid', 'false');
      }
    }
  }

  private clearFieldError(fieldName: string): void {
    this.displayFieldError(fieldName, '');
  }

  private validateForm(): boolean {
    const fields = ['origin', 'destination', 'departureDate', 'returnDate', 'passengers'];
    let isValid = true;

    fields.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    return isValid;
  }

  private displayGeneralError(message: string): void {
    if (this.generalErrorDiv) {
      this.generalErrorDiv.textContent = message;
      this.generalErrorDiv.style.display = message ? 'block' : 'none';
    }
  }

  private clearGeneralError(): void {
    this.displayGeneralError('');
  }

  private setLoadingState(loading: boolean): void {
    const submitText = this.submitButton.querySelector('.flight-search-form__submit-text') as HTMLElement;
    const loadingText = this.submitButton.querySelector('.flight-search-form__submit-loading') as HTMLElement;

    if (loading) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('btn--loading');
      if (submitText) submitText.style.display = 'none';
      if (loadingText) loadingText.style.display = 'inline';
    } else {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('btn--loading');
      if (submitText) submitText.style.display = 'inline';
      if (loadingText) loadingText.style.display = 'none';
    }
  }

  private async handleSubmit(): Promise<void> {
    this.clearGeneralError();

    if (!this.validateForm()) {
      this.displayGeneralError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    const searchData: FlightSearchData = {
      origin: this.originInput.value.trim(),
      destination: this.destinationInput.value.trim(),
      departureDate: new Date(this.departureDateInput.value),
      returnDate: this.returnDateInput.value ? new Date(this.returnDateInput.value) : undefined,
      passengers: parseInt(this.passengersSelect.value),
      class: 'economy' // Default class, could be extended with class selection
    };

    this.setLoadingState(true);

    try {
      if (this.onSearch) {
        await this.onSearch(searchData);
      }
    } catch (error) {
      console.error('Search error:', error);
      this.displayGeneralError('Une erreur est survenue lors de la recherche. Veuillez réessayer.');
    } finally {
      this.setLoadingState(false);
    }
  }

  public populateForm(data: FlightSearchData): void {
    if (this.originInput) this.originInput.value = data.origin;
    if (this.destinationInput) this.destinationInput.value = data.destination;
    if (this.departureDateInput) {
      const departureParts = data.departureDate.toISOString().split('T');
      this.departureDateInput.value = departureParts[0] || '';
    }
    
    if (data.returnDate && this.returnDateInput) {
      const returnParts = data.returnDate.toISOString().split('T');
      this.returnDateInput.value = returnParts[0] || '';
    }
    
    if (this.passengersSelect) {
      this.passengersSelect.value = data.passengers.toString();
    }
    
    // Update return date minimum
    this.updateReturnDateMin();
  }

  public getFormData(): FlightSearchData {
    return {
      origin: this.originInput.value.trim(),
      destination: this.destinationInput.value.trim(),
      departureDate: new Date(this.departureDateInput.value),
      returnDate: this.returnDateInput.value ? new Date(this.returnDateInput.value) : undefined,
      passengers: parseInt(this.passengersSelect.value),
      class: 'economy'
    };
  }

  public reset(): void {
    this.form.reset();
    this.clearGeneralError();
    
    // Clear all field errors
    ['origin', 'destination', 'departureDate', 'returnDate', 'passengers'].forEach(field => {
      this.clearFieldError(field);
    });
    
    // Reset min dates
    this.setMinDates();
  }
}